/**
 * Módulo `tirvu-face` (detecção + landmarks + alinhamento ArcFace) no browser.
 * Mesmos tipos e contrato do módulo nativo (modules/tirvu-face/index.ts).
 * A implementação web fica em ./tirvu-face-web (MediaPipe + warp de similaridade).
 */
import { detectarWeb, extrairTilesWeb, faceWebDisponivel } from "./tirvu-face-web";

export type Point = { x: number; y: number };
export type Box = { x: number; y: number; width: number; height: number };

export type FaceLandmarks = {
  leftEye: Point;
  rightEye: Point;
  mouthLeft: Point;
  mouthRight: Point;
  noseBase: Point | null;
};

export type DetectedFace = {
  box: Box;
  landmarks: FaceLandmarks | null;
  roll: number;
  yaw: number;
  pitch: number;
  leftEyeOpen: number | null;
  rightEyeOpen: number | null;
  smiling: number | null;
  interocularPx: number;
};

export type DetectResult = { faces: DetectedFace[]; width: number; height: number };
export type TileMode = "arcface" | "box";
export type TileSpec = { size: number; mode: TileMode; padding?: number; grayscale?: boolean };
export type Tile = { size: number; channels: 1 | 3; data: Uint8Array };

export const ARCFACE_TEMPLATE_112: Readonly<FaceLandmarks> = Object.freeze({
  leftEye: { x: 38.2946, y: 51.6963 },
  rightEye: { x: 73.5318, y: 51.5014 },
  mouthLeft: { x: 41.5493, y: 92.3655 },
  mouthRight: { x: 70.7299, y: 92.2041 },
  noseBase: null,
});

const tileByteLength = (spec: TileSpec): number => spec.size * spec.size * (spec.grayscale ? 1 : 3);

export const TirvuFace = {
  isAvailable: (): boolean => faceWebDisponivel(),

  detect: async (uri: string): Promise<DetectResult> => detectarWeb(uri),

  extractTiles: async (uri: string, face: DetectedFace, specs: TileSpec[]): Promise<Tile[]> => {
    if (!face.landmarks) throw new Error("Rosto sem landmarks — não dá para alinhar");
    const specsCompletos = specs.map((s) => ({ size: s.size, mode: s.mode, padding: s.padding ?? 0, grayscale: s.grayscale ?? false }));
    const blob = await extrairTilesWeb(uri, face.landmarks, face.box, specsCompletos);
    const esperado = specs.reduce((soma, s) => soma + tileByteLength(s), 0);
    if (blob.byteLength !== esperado) throw new Error(`TirvuFace.extractTiles devolveu ${blob.byteLength} bytes, esperado ${esperado}`);
    let offset = 0;
    return specs.map((spec) => {
      const n = tileByteLength(spec);
      const data = blob.subarray(offset, offset + n);
      offset += n;
      return { size: spec.size, channels: (spec.grayscale ? 1 : 3) as 1 | 3, data };
    });
  },

  ARCFACE_TEMPLATE_112,
  tileByteLength,
};
